import { useCallback, useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { Checkbox, Fab, useTheme } from '@mui/material';
import { CheckBoxOutlineBlank, CheckBox, Add } from '@mui/icons-material';
import { Control, FieldError, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useStyles from './styles';
import { Autocomplete, Input, Spacer } from '../../../../../../components';
import { StepperContentProps } from '.';
import { scheduleDataSchema, ServiceScheduleData } from '../../../../../../validations/service.validation';
import { Days, DaysMapper, fromHour } from '../../../../../../utils/time';
import ScheduleRules, { ScheduleRuleProp } from './ScheduleRules';
import { clone } from '../../../../../../utils/helpers';
import { ServiceOpenHour, ServiceOpenHourContent } from '../../../../../../entities';
import { delay } from '../../../../../../utils/objects';

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

type ScheduleProps = Omit<StepperContentProps, 'activeStep' | 'handleUpload' | 'file'>;

export const Schedule = ({
  service,
  shouldValidate,
  setShouldValidate,
  validate,
  handleSetSelectedService,
}: ScheduleProps): JSX.Element => {
  const styles = useStyles();
  const { spacing } = useTheme();
  const [rules, setRules] = useState<ScheduleRuleProp[]>([]);

  const {
    control,
    formState: { errors },
    setValue,
    register,
    reset,
    handleSubmit,
  } = useForm<ServiceScheduleData>({
    resolver: yupResolver(scheduleDataSchema),
    mode: 'onChange',
    defaultValues: {
      days: [],
      endAt: '',
      startAt: '',
    },
  });

  const days = useWatch({ control, name: 'days' });

  const formatOpenHours = useCallback((): ServiceOpenHour => {
    const rulesToFetch = clone(rules);

    return new ServiceOpenHour({
      monday: rulesToFetch.filter(({ day }) => day === Days.Monday).map((val) => new ServiceOpenHourContent(val)),
      tuesday: rulesToFetch.filter(({ day }) => day === Days.Tuesday).map((val) => new ServiceOpenHourContent(val)),
      wednesday: rulesToFetch.filter(({ day }) => day === Days.Wednesday).map((val) => new ServiceOpenHourContent(val)),
      thursday: rulesToFetch.filter(({ day }) => day === Days.Thursday).map((val) => new ServiceOpenHourContent(val)),
      friday: rulesToFetch.filter(({ day }) => day === Days.Friday).map((val) => new ServiceOpenHourContent(val)),
      saturday: rulesToFetch.filter(({ day }) => day === Days.Saturday).map((val) => new ServiceOpenHourContent(val)),
      sunday: rulesToFetch.filter(({ day }) => day === Days.Sunday).map((val) => new ServiceOpenHourContent(val)),
    });
  }, [rules]);

  /** That step doesnt have validate to handle next */
  useEffect(() => {
    if (shouldValidate) {
      delay(() => {
        handleSetSelectedService({
          ...service,
          openHours: formatOpenHours(),
        });
      }, 100);

      setShouldValidate(false);
      validate(true);
    }
  }, [shouldValidate, service, setShouldValidate, validate, handleSetSelectedService, formatOpenHours]);

  const populateRules = useCallback(() => {
    if (!service.openHours) {
      return;
    }

    setRules([]);
    const defaultRules: ScheduleRuleProp[] = [];

    Object.keys(service.openHours).forEach((key) => {
      const values = service.openHours[key as Days];

      if (values) {
        values.forEach((value) => {
          defaultRules.push({
            uuid: v4(),
            day: key as Days,
            duration: value.duration,
            startAt: value.startAt,
          });
        });
      }
    });

    setRules(defaultRules);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service.openHours]);

  useEffect(() => {
    populateRules();
  }, [populateRules]);

  const addScheduleRule = (values: ServiceScheduleData) => {
    const actualRules = clone(rules);

    values.days.forEach((day) => {
      const startAt = fromHour(values.startAt);
      const endAt = fromHour(values.endAt, { shouldConvertToMidnight: true });
      /** should permit reservations that duration is more than 24 hours */
      const duration = endAt - startAt > 0 ? endAt - startAt : endAt - startAt + fromHour('24:00');

      actualRules.push({
        day,
        uuid: v4(),
        startAt,
        duration,
      });
    });

    setRules(actualRules);

    reset();
  };

  const removeScheduleRule = (value: string) => {
    const actualRules = clone(rules);

    const newRules = actualRules.filter(({ uuid }) => uuid !== value);

    setRules(newRules);
  };

  return (
    <div className={styles.container}>
      <Autocomplete<Days, true>
        label="dias de funcionamento"
        multiple
        options={Object.keys(DaysMapper) as Days[]}
        value={days || []}
        size="small"
        placeholder="Ex: Domingo, Segunda..."
        noOptionsText="Nada aqui..."
        onChange={(e, data) => {
          setValue('days', data);
        }}
        getOptionLabel={(option) => DaysMapper[option]}
        renderOption={(props, option, { selected }) => (
          <li {...props} style={{ padding: spacing(1) }}>
            <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
            {DaysMapper[option]}
          </li>
        )}
        error={Boolean(errors.days)}
        helperText={errors.days ? (errors.days as unknown as FieldError).message : ' '}
      />
      <div className={styles.line}>
        <Input
          label="horário inicial"
          placeholder="Ex: 12:00"
          validationRegister={register('startAt')}
          control={control as unknown as Control}
          mask={{ mask: '00:00' }}
          error={Boolean(errors.startAt)}
          helperText={errors.startAt?.message || ' '}
        />
        <Input
          label="horário final"
          placeholder="Ex: 13:00"
          validationRegister={register('endAt')}
          control={control as unknown as Control}
          mask={{ mask: '00:00' }}
          error={Boolean(errors.endAt)}
          helperText={errors.endAt?.message || ' '}
        />
      </div>
      <div className={styles.line}>
        <Spacer />
        <Fab onClick={handleSubmit(addScheduleRule)} color="primary" size="medium" disableRipple disableFocusRipple>
          <Add />
        </Fab>
      </div>
      <ScheduleRules rules={rules} onClickRemoveRule={removeScheduleRule} />
    </div>
  );
};

export default Schedule;
